/**
 * spells.js — assembler (organism layer)
 *
 * Combines pure spell metadata (spellsConfig.json) with cast behaviours
 * (spells/castBehaviours.js) to produce the public SPELLS API.
 *
 * Nothing game-logic related lives here — this file is intentionally thin.
 */

import SPELL_CONFIG    from './spellsConfig.json';
import CAST_BEHAVIOURS from './spells/castBehaviours';

// ── Rarity system ─────────────────────────────────────────────────────────────

/** Weighted probability for each rarity tier during spell selection. */
export const RARITY_WEIGHTS = { common: 50, uncommon: 30, rare: 15, epic: 4, legendary: 1 };

/** Display labels. */
export const RARITY_LABELS = {
  common:    'Common',
  uncommon:  'Uncommon',
  rare:      'Rare',
  epic:      'Epic',
  legendary: 'Legendary',
};

/** Hex colour for each rarity (drives border + badge colour). */
export const RARITY_COLORS = {
  common:    '#9e9e9e',
  uncommon:  '#4caf50',
  rare:      '#2196f3',
  epic:      '#9c27b0',
  legendary: '#ff9800',
};

/**
 * Pick one spell from pool, with probability proportional to rarity weight.
 * Returns the chosen spell object.
 */
export function weightedSpellPick(pool) {
  const total = pool.reduce((s, sp) => s + (RARITY_WEIGHTS[sp.rarity] ?? 1), 0);
  let r = Math.random() * total;
  for (const sp of pool) {
    r -= RARITY_WEIGHTS[sp.rarity] ?? 1;
    if (r <= 0) return sp;
  }
  return pool[pool.length - 1];
}

// ── Spell catalogue ───────────────────────────────────────────────────────────

/**
 * All 23 spells — metadata from JSON config, cast behaviour from castBehaviours.
 * Each spell: { id, label, icon, rarity, desc, upgrades, cooldown, cast }
 */
export const SPELLS = SPELL_CONFIG.map(cfg => ({
  ...cfg,
  cast: CAST_BEHAVIOURS[cfg.id],
}));

/** Quick lookup by spell id. */
export const SPELL_MAP = Object.fromEntries(SPELLS.map(sp => [sp.id, sp]));
